import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { aiMealsAPI } from '../../services/aiMeals.api';
import { mealsAPI } from '../../services/meals.api';
import {
  fetchAiSuggestionsStart,
  fetchAiSuggestionsSuccess,
  fetchAiSuggestionsFailure,
  updateAiFilters,
  addToRecentMeals,
  toggleAiFilterPreference,
  toggleCuisinePreference,
  selectAiSuggestions,
  selectAiFilters,
  selectUserData,
  selectAiLoading
} from '../../store/slices/aiMealsSlice';
import { selectPreferences, selectUser } from '../../store/slices/authSlice';

// Material UI imports
import {
  Typography,
  Box,
  Container,
  Grid,
  Card,
  CardMedia,
  CardContent,
  CardActions,
  Button,
  Chip,
  ToggleButton,
  ToggleButtonGroup,
  Paper,
  Divider,
  IconButton,
  Tooltip,
  Fade,
  CircularProgress,
  useTheme,
  alpha,
  Skeleton,
  ButtonBase,
  Avatar,
  useMediaQuery,
  Switch,
  FormControlLabel,
  Slider,
  Rating,
  Badge,
  Alert
} from '@mui/material';

// Icons
import {
  Favorite,
  FavoriteBorder,
  SkipNext,
  Share,
  RestaurantMenu,
  AccessTime,
  FoodBank,
  LocalDining,
  TrendingUp,
  EmojiEvents,
  Restaurant,
  Spa,
  Fastfood,
  FilterAlt,
  Psychology,
  Bolt,
  FitnessCenter,
  LocalFireDepartment,
  AddCircle,
  SmartToy,
  Recommend,
  Whatshot,
  Info,
  BookmarkAdd
} from '@mui/icons-material';

const AIMealSuggestions = () => {
  const dispatch = useDispatch();
  const aiSuggestions = useSelector(selectAiSuggestions);
  const aiFilters = useSelector(selectAiFilters);
  const userData = useSelector(selectUserData);
  const preferences = useSelector(selectPreferences);
  const user = useSelector(selectUser);
  const aiLoading = useSelector(selectAiLoading);
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  
  // State for advanced filter panel
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [highlightedMeal, setHighlightedMeal] = useState(null);
  
  // Fetch AI suggestions when filters change
  useEffect(() => {
    const fetchAiSuggestions = async () => {
      try {
        dispatch(fetchAiSuggestionsStart());
        
        // Prepare userData with country from user profile
        const enhancedUserData = {
          ...userData,
          country: user?.country || 'India'  // Use user country from profile or default to India
        };
        
        console.log('User country for seasonal suggestions:', enhancedUserData.country);
        
        // Call the AI service with filters and enhanced user data
        const response = await aiMealsAPI.getAISuggestions(aiFilters, enhancedUserData);
        dispatch(fetchAiSuggestionsSuccess(response.data));
      } catch (error) {
        dispatch(fetchAiSuggestionsFailure(error.message));
      }
    };

    fetchAiSuggestions();
  }, [dispatch, aiFilters, userData]);

  // Handle filter changes
  const handleFilterChange = (filterType, value) => {
    dispatch(updateAiFilters({ [filterType]: value }));
  };
  
  const handleMealTypeChange = (event, newMealType) => {
    console.log('Changing meal type to:', newMealType);
    if (newMealType !== null) {
      dispatch(updateAiFilters({ mealType: newMealType }));
    }
  };
  
  const handleDietaryTypeChange = (event, newDietaryTypes) => {
    console.log('Changing dietary types to:', newDietaryTypes);
    // Ensure dietaryType is always an array to avoid type issues
    const dietaryArray = Array.isArray(newDietaryTypes) ? newDietaryTypes : [];
    dispatch(updateAiFilters({ dietaryType: dietaryArray }));
  };

  const handlePreferenceToggle = (preference) => {
    dispatch(toggleAiFilterPreference({ preference }));
  };
  
  const handleCuisineToggle = (cuisine) => {
    dispatch(toggleCuisinePreference(cuisine));
  };

  // Handle meal actions
  const handleSelect = (meal) => {
    setHighlightedMeal(meal.id);
    setTimeout(() => {
      dispatch(addToRecentMeals(meal));
      setHighlightedMeal(null);
    }, 800);
  };
  
  // Add meal to favorites
  const [savingFavorite, setSavingFavorite] = useState(false);
  const [savingFavoriteId, setSavingFavoriteId] = useState(null);
  const [favoriteAddedMessage, setFavoriteAddedMessage] = useState('');
  const [savedFavorites, setSavedFavorites] = useState([]);
  
  // Check which meals are already in favorites
  useEffect(() => {
    const checkSavedFavorites = async () => {
      try {
        const response = await mealsAPI.getFavorites();
        // Extract meal IDs from favorites
        const favoriteIds = response.data.map(fav => 
          fav.meal?.id || (fav.mealName && fav.mealName === fav.mealName)
        ).filter(Boolean);
        setSavedFavorites(favoriteIds);
      } catch (error) {
        console.error('Error checking saved favorites:', error);
      }
    };
    
    if (user && user.id) {
      checkSavedFavorites();
    }
  }, [user]);
  
  const handleAddToFavorites = async (meal) => {
    try {
      // Check if user is logged in
      const token = localStorage.getItem('token');
      if (!token) {
        console.error('No authentication token found');
        setFavoriteAddedMessage('Please log in to save favorites');
        return;
      }
      
      // Get user information from Redux state
      if (!user || !user.id) {
        console.error('User not found in Redux state', user);
        setFavoriteAddedMessage('Error: User not found. Please log in again');
        return;
      }
      
      console.log('User attempting to save favorite:', user.id);
      console.log('Meal data being saved:', meal);
      
      setSavingFavorite(true);
      setSavingFavoriteId(meal.id);
      
      // Try saving the meal to favorites
      const response = await mealsAPI.addAiMealToFavorites(meal);
      console.log('Server response:', response.data);
      
      // Update local state to show this meal is now favorited
      setSavedFavorites(prev => [...prev, meal.id]);
      
      // Show success message
      setFavoriteAddedMessage(`${meal.name} added to your Meal Vault!`);
      setTimeout(() => setFavoriteAddedMessage(''), 3000); // Clear message after 3 seconds
    } catch (error) {
      console.error('Error adding to favorites:', error);
      
      // More detailed error handling
      let errorMessage = 'Failed to add to favorites. Please try again.';
      
      if (error.response) {
        console.log('Error response:', error.response);
        if (error.response.status === 401) {
          errorMessage = 'Your session expired. Please log in again.';
        } else if (error.response.status === 400) {
          if (error.response.data.message === 'Meal already in favorites') {
            errorMessage = `${meal.name} is already in your favorites`;
          } else {
            errorMessage = error.response.data.message || errorMessage;
          }
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setFavoriteAddedMessage(errorMessage);
    } finally {
      setSavingFavorite(false);
      setSavingFavoriteId(null);
    }
  };

  // Format AI score to be more human friendly (1-5 stars)
  const formatAiScore = (score) => {
    // Convert the raw AI score (typically 0-50) to a 1-5 scale
    const normalizedScore = Math.max(1, Math.min(5, Math.round(score / 10)));
    return normalizedScore;
  };

  // Function to get a color for a tag
  const getTagColor = (tag) => {
    const tagMap = {
      'vegetarian': 'success',
      'vegan': 'success',
      'non-vegetarian': 'error',
      'gluten-free': 'info',
      'dairy-free': 'warning',
      'healthy': 'primary',
      'spicy': 'error',
      'sweet': 'secondary'
    };
    return tagMap[tag.toLowerCase()] || 'default';
  };

  // Function to get an icon based on dietary preference
  const getDietaryIcon = (type) => {
    switch(type.toLowerCase()) {
      case 'veg':
      case 'vegetarian':
        return <Spa fontSize="small" sx={{ mr: 0.5 }} />;
      case 'non-veg':
      case 'non-vegetarian':
        return <Fastfood fontSize="small" sx={{ mr: 0.5 }} />;
      default:
        return <LocalDining fontSize="small" sx={{ mr: 0.5 }} />;
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Welcome Header */}
      <Box sx={{ mb: 4, textAlign: 'center' }}>
        <Typography variant="h3" component="h1" fontWeight="bold" color="primary.main" gutterBottom>
          {`AI-Powered Meal Suggestions for ${user?.name?.split(' ')[0] || 'You'}`}
        </Typography>
        <Typography variant="h6" color="text.secondary">
          {new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
        </Typography>
        <Chip 
          icon={<SmartToy />} 
          label="AI-Powered Recommendations" 
          color="secondary" 
          sx={{ mt: 2, fontWeight: 'medium', fontSize: '0.9rem' }}
        />
      </Box>
      
      {/* Filters Section */}
      <Paper 
        elevation={3} 
        sx={{ 
          p: 3, 
          mb: 5, 
          borderRadius: 2,
          background: theme => `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${alpha(theme.palette.secondary.light, 0.1)} 100%)`,
        }}
      >
        <Grid container spacing={3} alignItems="center">
          <Grid item xs={12} md={4}>
            <Box display="flex" alignItems="center" justifyContent="center" mb={isSmallScreen ? 2 : 0}>
              <Psychology fontSize="large" color="secondary" sx={{ mr: 1.5 }} />
              <Typography variant="h5" fontWeight="bold">
                AI Meal Filters
              </Typography>
            </Box>
          </Grid>
          
          <Grid item xs={12} md={4}>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: isSmallScreen ? 'center' : 'flex-start', mb: isSmallScreen ? 2 : 0 }}>
              <Typography variant="subtitle1" fontWeight="medium" color="text.secondary" gutterBottom>
                Meal Type
              </Typography>
              <ToggleButtonGroup
                value={aiFilters.mealType}
                exclusive
                onChange={handleMealTypeChange}
                aria-label="meal type"
                size="medium"
                color="secondary"
                sx={{ flexWrap: 'wrap', justifyContent: 'center' }}
              >
                {preferences && preferences.mealTypes && preferences.mealTypes.map((type) => (
                  <ToggleButton key={type} value={type} aria-label={type} sx={{ borderRadius: '16px', m: 0.5, textTransform: 'capitalize' }}>
                    {type === 'breakfast' && <AccessTime fontSize="small" sx={{ mr: 1 }} />}
                    {type === 'lunch' && <FoodBank fontSize="small" sx={{ mr: 1 }} />}
                    {type === 'dinner' && <Restaurant fontSize="small" sx={{ mr: 1 }} />}
                    {type}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
            </Box>
          </Grid>
          
          <Grid item xs={12} md={4}>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: isSmallScreen ? 'center' : 'flex-start' }}>
              <Typography variant="subtitle1" fontWeight="medium" color="text.secondary" gutterBottom>
                Dietary Preference
              </Typography>
              <ToggleButtonGroup
                value={aiFilters.dietaryType}
                onChange={handleDietaryTypeChange}
                aria-label="dietary preference"
                size="medium"
                color="secondary"
                sx={{ flexWrap: 'wrap', justifyContent: 'center' }}
              >
                {preferences && preferences.dietaryType && preferences.dietaryType.map((type) => (
                  <ToggleButton key={type} value={type} aria-label={type} sx={{ borderRadius: '16px', m: 0.5, textTransform: 'capitalize' }}>
                    {type === 'vegetarian' && getDietaryIcon('veg')}
                    {type === 'non-vegetarian' && getDietaryIcon('non-veg')}
                    {type}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
            </Box>
          </Grid>
        </Grid>
        
        {/* Advanced AI Filtering Options */}
        <Box sx={{ mt: 3 }}>
          <Button 
            variant="outlined" 
            color="secondary" 
            startIcon={<FilterAlt />}
            onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
            sx={{ mb: 2 }}
          >
            {showAdvancedFilters ? "Hide Advanced AI Filters" : "Show Advanced AI Filters"}
          </Button>
          
          <Fade in={showAdvancedFilters}>
            <Grid container spacing={3} sx={{ mt: 1 }}>
              <Grid item xs={12} md={4}>
                <Paper elevation={1} sx={{ p: 2, height: '100%' }}>
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                    <Bolt color="secondary" sx={{ mr: 1, verticalAlign: 'middle' }} />
                    Quick Meal Preference
                  </Typography>
                  <FormControlLabel
                    control={
                      <Switch 
                        checked={userData.preferences.preferQuickMeals} 
                        onChange={() => handlePreferenceToggle('preferQuickMeals')}
                        color="secondary"
                      />
                    }
                    label="Prefer quick meals (under 30 min)"
                  />
                </Paper>
              </Grid>
              
              <Grid item xs={12} md={4}>
                <Paper elevation={1} sx={{ p: 2, height: '100%' }}>
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                    <FitnessCenter color="secondary" sx={{ mr: 1, verticalAlign: 'middle' }} />
                    Nutritional Preferences
                  </Typography>
                  <FormControlLabel
                    control={
                      <Switch 
                        checked={userData.preferences.highProtein} 
                        onChange={() => handlePreferenceToggle('highProtein')}
                        color="secondary"
                      />
                    }
                    label="High protein meals"
                  />
                  <FormControlLabel
                    control={
                      <Switch 
                        checked={userData.preferences.lowCalorie} 
                        onChange={() => handlePreferenceToggle('lowCalorie')}
                        color="secondary"
                      />
                    }
                    label="Low calorie options"
                  />
                </Paper>
              </Grid>
              
              <Grid item xs={12} md={4}>
                <Paper elevation={1} sx={{ p: 2, height: '100%' }}>
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                    <Restaurant color="secondary" sx={{ mr: 1, verticalAlign: 'middle' }} />
                    Cuisine Preferences
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                    {['indian', 'north-indian', 'south-indian', 'chinese', 'italian', 'mexican'].map(cuisine => (
                      <Chip
                        key={cuisine}
                        label={cuisine.replace('-', ' ')}
                        color={userData.preferences.favoriteCuisines.includes(cuisine) ? 'secondary' : 'default'}
                        variant={userData.preferences.favoriteCuisines.includes(cuisine) ? 'filled' : 'outlined'}
                        onClick={() => handleCuisineToggle(cuisine)}
                        sx={{ textTransform: 'capitalize' }}
                      />
                    ))}
                  </Box>
                </Paper>
              </Grid>
            </Grid>
          </Fade>
        </Box>
      </Paper>

      {/* Success Message */}
      {favoriteAddedMessage && (
        <Fade in={Boolean(favoriteAddedMessage)}>
          <Alert 
            severity="success" 
            sx={{ mb: 3, boxShadow: 2 }}
            onClose={() => setFavoriteAddedMessage('')}
          >
            {favoriteAddedMessage}
          </Alert>
        </Fade>
      )}

      {/* AI Meal Suggestions Heading */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box display="flex" alignItems="center">
          <Recommend fontSize="large" color="secondary" sx={{ mr: 1.5 }} />
          <Typography variant="h4" fontWeight="bold">
            AI-Powered Suggestions
          </Typography>
        </Box>
        
        <Chip 
          icon={<Whatshot />} 
          label={aiFilters.mealType ? `Best ${aiFilters.mealType} ideas` : "Personalized for you"} 
          color="secondary" 
          variant="outlined" 
          sx={{ fontWeight: 'medium' }}
        />
      </Box>
      
      {/* Loading State */}
      {aiLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 8 }}>
          <CircularProgress color="secondary" size={60} />
        </Box>
      )}

      {/* AI Meal Suggestions Grid */}
      {!aiLoading && (
        <Grid container spacing={3}>
          {aiSuggestions && aiSuggestions.length > 0 ? aiSuggestions.map((meal) => (
            <Grid item xs={12} sm={6} md={4} key={meal.id}>
              <Card 
                elevation={4} 
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  position: 'relative',
                  transition: 'transform 0.3s, box-shadow 0.3s',
                  borderRadius: 2,
                  overflow: 'hidden',
                  '&:hover': {
                    transform: 'translateY(-5px)',
                    boxShadow: 8,
                  },
                  ...(highlightedMeal === meal.id && {
                    outline: `2px solid ${theme.palette.secondary.main}`,
                    boxShadow: 8,
                  }),
                }}
              >
                {/* AI Score Badge */}
                <Box sx={{ position: 'absolute', top: 12, right: 12, zIndex: 2 }}>
                  <Tooltip 
                    title={
                      <>
                        <Typography variant="subtitle2">AI Match Score</Typography>
                        <Typography variant="body2" component="div">
                          {meal.aiReasons && meal.aiReasons.map((reason, i) => (
                            <Box key={i} sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                              <Bolt fontSize="small" sx={{ mr: 0.5 }} />
                              {reason}
                            </Box>
                          ))}
                        </Typography>
                      </>
                    } 
                    arrow
                  >
                    <Paper elevation={3} sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      borderRadius: '12px', 
                      px: 1, 
                      py: 0.5, 
                      backgroundColor: alpha(theme.palette.secondary.main, 0.8)
                    }}>
                      <SmartToy sx={{ color: 'white', fontSize: 18, mr: 0.5 }} />
                      <Rating 
                        value={formatAiScore(meal.aiScore)} 
                        readOnly 
                        precision={0.5} 
                        size="small" 
                        sx={{ color: 'white' }}
                      />
                    </Paper>
                  </Tooltip>
                </Box>
                
                {/* Meal Image */}
                <Box sx={{ position: 'relative', paddingTop: '56.25%' /* 16:9 Aspect Ratio */ }}>
                  {aiLoading ? (
                    <Skeleton 
                      variant="rectangular" 
                      sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                      }}
                    />
                  ) : (
                    <CardMedia
                      component="img"
                      image={meal.imageUrl || `https://source.unsplash.com/random/400x300/?food,${meal.name.replace(' ', ',')}`}
                      alt={meal.name}
                      onError={(e) => {
                        // If image fails to load, replace with a fallback
                        e.target.onerror = null;
                        e.target.src = `https://source.unsplash.com/random/400x300/?food,${meal.name.replace(' ', ',')}`;
                      }}
                      sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                      }}
                    />
                  )}
                </Box>
                
                <CardContent sx={{ flexGrow: 1, pb: 1 }}>
                  <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                    <Typography variant="h6" fontWeight="bold" gutterBottom>
                      {meal.name}
                    </Typography>
                  </Box>
                  
                  <Typography variant="body2" color="text.secondary" paragraph sx={{ mb: 1 }}>
                    {meal.description}
                  </Typography>
                  
                  <Box display="flex" flexWrap="wrap" gap={0.5} mb={1}>
                    {/* Dietary Type */}
                    {meal.dietaryType.map((type) => (
                      <Chip
                        key={type}
                        size="small"
                        icon={getDietaryIcon(type)}
                        label={type}
                        color={type === 'vegetarian' ? 'success' : 'default'}
                        variant="outlined"
                        sx={{ borderRadius: '12px' }}
                      />
                    ))}
                    
                    {/* Prep Time */}
                    <Chip
                      size="small"
                      icon={<AccessTime fontSize="small" />}
                      label={`${meal.preparationTime} min`}
                      variant="outlined"
                      sx={{ borderRadius: '12px' }}
                    />
                    
                    {/* Calories */}
                    <Chip
                      size="small"
                      icon={<LocalFireDepartment fontSize="small" />}
                      label={`${meal.calories} cal`}
                      variant="outlined"
                      sx={{ borderRadius: '12px' }}
                    />
                  </Box>
                  
                  {/* Meal Tags */}
                  {meal.tags && meal.tags.length > 0 && (
                    <Box display="flex" flexWrap="wrap" gap={0.5}>
                      {meal.tags.slice(0, 3).map((tag) => (
                        <Chip
                          key={tag}
                          size="small"
                          label={tag.replace('-', ' ')}
                          color={getTagColor(tag)}
                          sx={{ 
                            height: '20px', 
                            fontSize: '0.7rem',
                            borderRadius: '12px',
                          }}
                        />
                      ))}
                    </Box>
                  )}
                </CardContent>
                
                <CardActions sx={{ p: 2, pt: 0 }}>
                  <Grid container spacing={1}>
                    <Grid item xs={12}>
                      <Button
                        variant="contained"
                        fullWidth
                        color="secondary"
                        startIcon={<AddCircle />}
                        onClick={() => handleSelect(meal)}
                        sx={{
                          borderRadius: '12px',
                          boxShadow: 2,
                        }}
                      >
                        Select this meal
                      </Button>
                    </Grid>
                    <Grid item xs={6}>
                      <Button
                        fullWidth
                        startIcon={
                          savingFavorite && savingFavoriteId === meal.id ? 
                            <CircularProgress size={16} color="inherit" /> : 
                            savedFavorites.includes(meal.id) ? 
                              <Favorite color="error" /> : 
                              <BookmarkAdd />
                        }
                        size="small"
                        variant={savedFavorites.includes(meal.id) ? "contained" : "outlined"}
                        color={savedFavorites.includes(meal.id) ? "error" : "primary"}
                        onClick={() => !savedFavorites.includes(meal.id) && handleAddToFavorites(meal)}
                        disabled={savingFavorite || savedFavorites.includes(meal.id)}
                        sx={{
                          borderRadius: '12px',
                          '&.Mui-disabled': {
                            opacity: savedFavorites.includes(meal.id) ? 1 : 0.7,
                            color: savedFavorites.includes(meal.id) ? 'white' : undefined,
                            backgroundColor: savedFavorites.includes(meal.id) ? theme.palette.error.main : undefined
                          }
                        }}
                      >
                        {savedFavorites.includes(meal.id) ? 'Saved' : 'Save'}
                      </Button>
                    </Grid>
                    <Grid item xs={6}>
                      <Button
                        fullWidth
                        startIcon={<SkipNext />}
                        size="small"
                        sx={{ borderRadius: '12px' }}
                      >
                        Next
                      </Button>
                    </Grid>
                  </Grid>
                </CardActions>
              </Card>
            </Grid>
          )) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%', py: 8 }}>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No meal suggestions found for your current filters
              </Typography>
              <Button 
                variant="contained" 
                color="secondary"
                startIcon={<FilterAlt />}
                onClick={() => {
                  dispatch(updateAiFilters({ mealType: '', dietaryType: [] }));
                }}
                sx={{ mt: 2 }}
              >
                Reset Filters
              </Button>
            </Box>
          )}
        </Grid>
      )}
    </Container>
  );
};

export default AIMealSuggestions;
